import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet as WalletIcon, Check, Lock, RefreshCw, Plus, X, CreditCard } from 'lucide-react';
import { walletService } from '../../services/walletService';

const QUICK_AMOUNTS = [100, 200, 500, 1000];

const Wallet: React.FC = () => {
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState('');
  const [showTopUp, setShowTopUp] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  // Last balance we showed, so a rise after returning from Paystack can be
  // recognised as a completed top-up. CustomerDashboard already called
  // verifyTopUp on the callback return; this is purely how the Wallet notices.
  const lastBalanceRef = useRef<number | null>(null);

  const refreshBalance = useCallback(async (announce = false) => {
    try {
      const next = await walletService.getBalance();
      if (announce && lastBalanceRef.current !== null && next > lastBalanceRef.current) {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 4000);
      }
      lastBalanceRef.current = next;
      setBalance(next);
    } catch { /* keep whatever we last showed rather than blanking the card */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    refreshBalance();
    // Coming back from a top-up, CustomerDashboard routes here and kicks off
    // verifyTopUp (which credits the balance itself rather than waiting solely
    // on the webhook). One more check covers the lag between that verify call
    // landing and the first fetch above.
    const t = setTimeout(() => refreshBalance(true), 2000);
    return () => clearTimeout(t);
  }, [refreshBalance]);

  const handleTopUp = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return;
    setError('');

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const email = user.email || `${user.phone || 'customer'}@safaricab.app`;

    setRedirecting(true);
    try {
      // Opens Paystack's own hosted checkout page rather than the inline widget
      // script (window.PaystackPop) - the same approach already used reliably for
      // ride card payments and scheduled-trip prepay elsewhere in this app, so it
      // doesn't depend on a third-party script loading.
      // Always pass a real callback_url and do a plain in-webview redirect -
      // exactly the pattern the ride-payment/scheduled-trip flows already use
      // successfully on native, thanks to capacitor.config.ts's allowNavigation
      // whitelisting Paystack's domains.
      const callbackUrl = `${window.location.origin}${window.location.pathname}?wallet_topup=1`;
      const initData = await walletService.initializeTopUp(amt, email, user.id, callbackUrl);
      setRedirecting(false);
      window.location.href = initData.authorization_url;
    } catch (err: any) {
      setRedirecting(false);
      setError(err?.message || 'Could not open payment. Please try again.');
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-4 pt-4 pb-24">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-white font-bold text-xl">Wallet</h2>
        <button onClick={() => refreshBalance()} className="text-white/40 hover:text-white" aria-label="Refresh balance">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className="bg-green-500/20 border border-green-500/50 rounded-xl p-4 mb-4 flex items-center gap-3"
          >
            <Check className="w-5 h-5 text-green-500" />
            <span className="text-green-400 text-sm">Top-up successful!</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Balance hero - same treatment as the driver app's wallet, so the two
          apps read as one product. Skeleton (not "...") on first load, so an
          empty wallet and a still-loading one never look alike. */}
      <div className="bg-safaricab-gold/10 border border-safaricab-gold/30 rounded-2xl p-6 mb-4 text-center">
        <WalletIcon className="w-10 h-10 text-safaricab-gold mx-auto mb-3" />
        <p className="text-white/60 text-sm mb-1">Wallet balance</p>
        {loading && balance === null ? (
          <div className="h-10 w-40 mx-auto bg-white/10 rounded-lg animate-pulse" />
        ) : (
          <p className="text-safaricab-gold font-bold text-4xl">KES {(balance || 0).toLocaleString()}</p>
        )}
        <p className="text-white/30 text-xs mt-2">Available to spend on rides</p>
      </div>

      {/* Top Up */}
      {!showTopUp ? (
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={() => { setShowTopUp(true); setError(''); }}
          className="w-full bg-safaricab-gold text-black font-bold py-4 rounded-xl flex items-center justify-center gap-2 mb-6"
        >
          <Plus className="w-5 h-5" /> Top Up
        </motion.button>
      ) : (
        <motion.div
          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
          className="overflow-hidden mb-6"
        >
          <div className="bg-safaricab-card rounded-xl p-4 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <p className="text-white font-bold text-sm">Top up your wallet</p>
              <button
                onClick={() => { setShowTopUp(false); setAmount(''); setError(''); }}
                className="text-white/40 hover:text-white"
                aria-label="Cancel top-up"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <label className="text-white/60 text-xs font-medium uppercase">Amount (KES)</label>
            <div className="relative mt-2 mb-3">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 font-bold">KES</span>
              <input
                type="number"
                value={amount}
                onChange={(e) => { setAmount(e.target.value); setError(''); }}
                placeholder="0"
                min="0"
                autoFocus
                className="w-full bg-safaricab-dark border border-white/10 rounded-xl py-4 pl-16 pr-4 text-white text-2xl font-bold outline-none focus:border-safaricab-gold/50"
              />
            </div>

            <div className="flex gap-2 mb-4">
              {QUICK_AMOUNTS.map((amt) => (
                <button
                  key={amt}
                  onClick={() => { setAmount(amt.toString()); setError(''); }}
                  className={`flex-1 border rounded-xl py-3 text-white text-sm font-medium ${
                    amount === amt.toString() ? 'border-safaricab-gold bg-safaricab-gold/10' : 'border-white/10 bg-safaricab-dark'
                  }`}
                >
                  {amt}
                </button>
              ))}
            </div>

            {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={handleTopUp}
              disabled={!amount || Number(amount) <= 0 || redirecting}
              className="w-full bg-safaricab-gold text-black font-bold py-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-40"
            >
              {redirecting
                ? <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                : <Lock className="w-5 h-5" />}
              {redirecting ? 'Redirecting...' : `Pay ${amount ? `KES ${Number(amount).toLocaleString()}` : ''}`.trim()}
            </motion.button>
            <p className="text-white/30 text-xs text-center mt-3">Secured by Paystack</p>
          </div>
        </motion.div>
      )}

      <div className="bg-white/5 rounded-xl p-4 flex items-center gap-3">
        <CreditCard className="w-5 h-5 text-safaricab-gold flex-shrink-0" />
        <p className="text-white/60 text-xs">
          Select <span className="text-safaricab-gold font-semibold">Wallet</span> under Payment Methods to pay for rides from this balance.
        </p>
      </div>
    </motion.div>
  );
};

export default Wallet;
